import { Item, ItemsResponseDTO } from '@dto/architecture.dto';
import { Form } from "antd";
import { FormProviderProps } from 'antd/es/form/context';
import { useContext } from 'react';
import { XmSelect } from "src/component/select/XmSelect";
import { isClassLPAR } from "src/data/Class";
import useGetApiData from "src/hook/useGetApiData";
import { makeItemLabelRoot } from "../children/Items";
import { AllItemsContext } from '../comp/AllItems';
import { CustomFormCompProps } from '../comp/Items';
import ModalSelectItems from '../comp/ModalSelectItems';
import TagsList from '../comp/TagsList';
import { Option } from '../hook/useParentTypes';
import useSelectModal from "../hook/useSelectModal";
import { AdditionalApiPropsContext, makeItemOfList, RuleContext } from '../XmRule';
import { RuleGroup } from "../XmRuleList";
import AddButton from './AddButton';

export interface FormSelectData {
  subsystem: string;
  items: Item[];
}

export interface FormCompProps<Value> extends CustomFormCompProps<Value> {
  parentTypes: Option[];
}

const Items = ({ value, onChange, parentTypes }: FormCompProps<RuleGroup['parents']['items']>) => {
  const { cls } = useContext(AllItemsContext);
  const { filterByParents } = useContext(RuleContext);
  const handleFormFinish: FormProviderProps['onFormFinish'] = (_, { forms }) => {
    const { items }: FormSelectData = forms['selected-items'].getFieldsValue(true);
    onChange?.(items);
    filterByParents();
  };

  const [showModalSelectParent, contextHolder] = useSelectModal<Item[]>({
    onFormFinish: handleFormFinish,
  });

  const handleAdd = () => {
    showModalSelectParent({
      initialValues: { items: value, subsystem: undefined },
      content: <ModalSelectItemsParents parentTypes={parentTypes} />
    });
  };

  const handleCloseItem = (id: string) => () => {
    onChange?.(value?.filter(item => item.item_id !== id));
    filterByParents();
  };

  return <>
    <TagsList items={value?.map(makeItemOfList(cls))} onClose={handleCloseItem} />
    {contextHolder}
    <AddButton onClick={handleAdd} disabled={!value} />
  </>;
};

export interface FormSelectData {
  subsystem: string;
  items: Item[];
}

function ModalSelectItemsParents({ parentTypes }: { parentTypes: Option[]; }) {
  const { cls, subsystem, hw_type } = useContext(AllItemsContext);
  const { parents: parentsParams } = useContext(AdditionalApiPropsContext);
  const form = Form.useFormInstance<FormSelectData>();
  const activeSelectOption = Form.useWatch<string>('subsystem', form);
  const [activeParentType] = activeSelectOption ? activeSelectOption.split("/") : [null];
  let apiParm: Record<string, unknown> = { class: cls, subsystem: activeParentType, strict_acl: false };
  if (activeParentType === 'vm_folder' || !isClassLPAR(cls) && activeParentType !== 'device')
    apiParm.withRootParent = true;

  if (parentsParams?.select)
    apiParm = { ...apiParm, ...parentsParams.select };

  const [possibleParents, , isLoadingPossibleParent] = useGetApiData<Item[], ItemsResponseDTO>(
    `/api/architecture/v1/items`,
    `Failed to get parent options`,
    [activeSelectOption, cls, hw_type, subsystem],
    [],
    !!activeParentType ? 'YES' : 'NO',
    true,
    isClassLPAR(cls) ? { ...apiParm, hw_type: hw_type !== cls ? hw_type : activeSelectOption } : { ...apiParm, childSubsystem: subsystem }
  );

  function makeItemOfList({ label, item_id, subsystem }: Item) {
    return hw_type ? { key: item_id, title: `${subsystem}/${label}` } : { key: item_id, title: label };
  }

  return (
    <ModalSelectItems<Item, FormSelectData>
      makeItemOfList={makeItemOfList}
      makeLabel={makeItemLabelRoot}
      isLoading={isLoadingPossibleParent}
      treeItems={possibleParents}
      handleCheckItem={(item) => item}
      treeSelectProps={{ disabled: !activeSelectOption, loading: isLoadingPossibleParent }}
    >
      <Form.Item name='subsystem' noStyle>
        <XmSelect
          size='middle'
          placeholder="Subsystem"
          options={parentTypes.map(parent => ({
            ...parent,
            value: parent.hwType ? `${parent.value}/${parent.hwType}` : parent.value,
          }))}
          ignoreReadonly
        />
      </Form.Item>
    </ModalSelectItems>
  );
}

export default Items;
